import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/analytics?range=${timeRange}`);
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error('Error loading analytics data');
      // Fallback to empty data structure
      setAnalyticsData({
        userGrowth: { current: 0, previous: 0, growth: 0 },
        revenueGrowth: { current: 0, previous: 0, growth: 0 },
        listingGrowth: { current: 0, previous: 0, growth: 0 },
        engagementRate: { current: 0, previous: 0, growth: 0 },
        topCategories: [],
        topLocations: []
      });
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, current, previous, growth, icon, color }) => (
    <div className="bg-white rounded-3xl shadow-xl p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: color }}>
          <i className={`${icon} text-white text-xl`}></i>
        </div>
        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold ${
          growth > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <i className={`fas fa-arrow-${growth > 0 ? 'up' : 'down'}`}></i>
          <span>{Math.abs(growth)}%</span>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-2" style={{ color: '#393E46' }}>{title}</h3>
        <p className="text-3xl font-bold mb-1" style={{ color: '#222831' }}>{current.toLocaleString()}</p>
        <p className="text-sm" style={{ color: '#393E46' }}>
          Previous: {previous.toLocaleString()}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#393E46' }}>
              <i className="fas fa-chart-bar text-white text-lg lg:text-xl"></i>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>
              Platform Analytics
            </h2>
          </div>
          <p className="ml-13 lg:ml-15 text-sm lg:text-base" style={{ color: '#393E46' }}>Comprehensive insights and performance metrics</p>
        </div>
        <div className="mt-4 lg:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="w-full lg:w-auto px-4 lg:px-6 py-3 border-2 rounded-2xl font-medium shadow-lg text-sm lg:text-base"
            style={{ 
              borderColor: '#DFD0B8', 
              backgroundColor: '#DFD0B8',
              color: '#222831'
            }}
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard
          title="Total Users"
          current={analyticsData.userGrowth?.current || 0}
          previous={analyticsData.userGrowth?.previous || 0}
          growth={analyticsData.userGrowth?.growth || 0}
          icon="fas fa-users"
          color="#222831"
        />
        <MetricCard
          title="Revenue (₹)"
          current={analyticsData.revenueGrowth?.current || 0}
          previous={analyticsData.revenueGrowth?.previous || 0}
          growth={analyticsData.revenueGrowth?.growth || 0}
          icon="fas fa-rupee-sign"
          color="#948979"
        />
        <MetricCard
          title="Total Listings"
          current={analyticsData.listingGrowth?.current || 0}
          previous={analyticsData.listingGrowth?.previous || 0}
          growth={analyticsData.listingGrowth?.growth || 0}
          icon="fas fa-list"
          color="#393E46"
        />
        <MetricCard
          title="Engagement Rate (%)"
          current={analyticsData.engagementRate?.current || 0}
          previous={analyticsData.engagementRate?.previous || 0}
          growth={analyticsData.engagementRate?.growth || 0}
          icon="fas fa-heart"
          color="#948979"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Top Categories */}
        <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-tags text-white text-sm lg:text-lg"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Top Categories</h3>
          </div>
          <div className="space-y-3 lg:space-y-4">
            {analyticsData.topCategories?.map((category, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-xl flex items-center justify-center text-white font-bold text-xs lg:text-sm" style={{ backgroundColor: '#393E46' }}>
                    {index + 1}
                  </div>
                  <span className="font-medium text-sm lg:text-base" style={{ color: '#222831' }}>{category.name}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-16 lg:w-24 h-2 rounded-full" style={{ backgroundColor: '#DFD0B8' }}>
                    <div 
                      className="h-2 rounded-full" 
                      style={{ 
                        backgroundColor: '#948979', 
                        width: `${category.percentage}%` 
                      }}
                    ></div>
                  </div>
                  <span className="font-bold text-xs lg:text-sm min-w-0" style={{ color: '#393E46' }}>{category.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Locations */}
        <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-map-marker-alt text-white text-sm lg:text-lg"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Top Locations</h3>
          </div>
          <div className="space-y-3 lg:space-y-4">
            {analyticsData.topLocations?.map((location, index) => (
              <div key={index} className="p-3 lg:p-4 rounded-2xl border-2" style={{ borderColor: '#DFD0B8' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-xl flex items-center justify-center text-white font-bold text-xs lg:text-sm" style={{ backgroundColor: '#948979' }}>
                      {index + 1}
                    </div>
                    <span className="font-bold text-sm lg:text-lg" style={{ color: '#222831' }}>{location.name}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 lg:gap-4 text-xs lg:text-sm">
                  <div>
                    <span style={{ color: '#393E46' }}>Users: </span>
                    <span className="font-bold" style={{ color: '#222831' }}>{location.users}</span>
                  </div>
                  <div>
                    <span style={{ color: '#393E46' }}>Revenue: </span>
                    <span className="font-bold" style={{ color: '#948979' }}>₹{location.revenue.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Placeholders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-chart-line text-white text-lg"></i>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>User Growth Trend</h3>
          </div>
          <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-2xl" style={{ borderColor: '#DFD0B8' }}>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
                <i className="fas fa-chart-line text-3xl" style={{ color: '#948979' }}></i>
              </div>
              <h4 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Growth Chart</h4>
              <p style={{ color: '#393E46' }}>Interactive user growth visualization</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#393E46' }}>
              <i className="fas fa-chart-pie text-white text-lg"></i>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Revenue Distribution</h3>
          </div>
          <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-2xl" style={{ borderColor: '#DFD0B8' }}>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
                <i className="fas fa-chart-pie text-3xl" style={{ color: '#948979' }}></i>
              </div>
              <h4 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Revenue Pie Chart</h4>
              <p style={{ color: '#393E46' }}>Revenue breakdown by categories</p>
            </div>
          </div>
        </div>
      </div>

      {/* Insights Section */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
            <i className="fas fa-lightbulb text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Key Insights</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="flex items-center space-x-3 mb-3">
              <i className="fas fa-trending-up text-2xl" style={{ color: '#948979' }}></i>
              <h4 className="font-bold" style={{ color: '#222831' }}>Growth Trend</h4>
            </div>
            <p className="text-sm" style={{ color: '#393E46' }}>
              User registration has increased by 13.6% this month, indicating strong platform adoption.
            </p>
          </div>
          <div className="p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="flex items-center space-x-3 mb-3">
              <i className="fas fa-star text-2xl" style={{ color: '#948979' }}></i>
              <h4 className="font-bold" style={{ color: '#222831' }}>Popular Category</h4>
            </div>
            <p className="text-sm" style={{ color: '#393E46' }}>
              Evening wear remains the most popular category, accounting for 32% of all listings.
            </p>
          </div>
          <div className="p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="flex items-center space-x-3 mb-3">
              <i className="fas fa-map-marker-alt text-2xl" style={{ color: '#948979' }}></i>
              <h4 className="font-bold" style={{ color: '#222831' }}>Top Market</h4>
            </div>
            <p className="text-sm" style={{ color: '#393E46' }}>
              Mumbai leads in both user count and revenue generation, showing strong market presence.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Analytics;